import { useLazyContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { ParcelContext } from "@redotech/redo-merchant-fulfillment-app/services/parcel-service";
import { RcoCheckoutPackageStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  Parcel,
  ParcelType,
} from "@redotech/redo-model/outbound-labels/parcel";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { WithRequiredFields } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "./index";

const RcoCheckoutPackageInspector = memo(function RcoCheckoutPackageInspector({
  step,
  setStep,
  onClose,
}: StepTypeDetailsProps<PartialWithRequiredType<RcoCheckoutPackageStep>>) {
  const { viewPackagesUrl } = useViewPackagesExternalLink();
  const { parcelOptions, setParcel } = useParcelOptions({ step, setStep });

  return (
    <Flex dir="column">
      <Flex align="center" justify="space-between" pt="md">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Package
        </Text>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={XIcon}
          onClick={onClose}
          size="sm"
        />
      </Flex>
      <Flex dir="column" gap="xl" justify="flex-start" py="xl">
        <RedoSingleSelectDropdownInput
          label="Select package"
          options={parcelOptions || []}
          optionSelected={({ item }) => setParcel(item)}
          selectedItem={parcelOptions?.find((o) => o.id === step.parcelId)}
          size="xs"
        />
        {viewPackagesUrl && (
          <RedoExternalLink text="View packages" url={viewPackagesUrl} />
        )}
      </Flex>
    </Flex>
  );
});

function useParcelOptions({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageStep>;
  setStep: (step: PartialWithRequiredType<RcoCheckoutPackageStep>) => void;
}) {
  const [parcelsLoad, _] = useLazyContext(ParcelContext);

  const parcelOptions: TextRedoListItem<string>[] | undefined = useMemo(() => {
    return parcelsLoad.value
      ?.filter((parcel): parcel is WithRequiredFields<Parcel, "_id"> =>
        isDef(parcel._id),
      )
      .map((parcel) => ({
        type: "text",
        value: parcel.name,
        text: parcel.name,
        id: parcel._id,
      }));
  }, [parcelsLoad.value]);

  const setParcel = useHandler((item: RedoListItem<string>) => {
    const parcel = parcelsLoad.value?.find((o) => o._id === item.id);
    if (item.id && typeof item.id === "string" && parcel) {
      const dimensionsDescription = (() => {
        if (parcel.parcelType === ParcelType.CUSTOM) {
          return `${parcel.length} x ${parcel.width} x ${parcel.height} ${parcel.lengthUnit}`;
        }
        return parcel.dimensions[0];
      })();
      setStep({
        ...step,
        parcelId: item.id,
        name: parcel.name,
        dimensions: dimensionsDescription,
      });
    }
  });

  return { parcelOptions, setParcel };
}

function useViewPackagesExternalLink() {
  const location = useLocation();

  const viewPackagesUrl = useMemo(() => {
    const teamId = location.pathname.match(/stores\/([0-9a-fA-F]+)\//)?.[1];
    if (!teamId) {
      return undefined;
    }

    return `/stores/${teamId}/settings/oms/packages`;
  }, [location.pathname]);

  return { viewPackagesUrl };
}

export const rcoCheckoutPackageStepType: FrontendStepType<
  PartialWithRequiredType<RcoCheckoutPackageStep>
> = {
  type: "custom",
  valid: (step) => Boolean(step.parcelId),
  customInspector: RcoCheckoutPackageInspector,
};
